import React, { ChangeEvent, useContext, useEffect, useState } from 'react';

import aguid from 'lib/aguid';

import SingleMetricDateMatrixForm from './SingleMetricDateMatrixForm';
import { FiscalPeriodsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider';

const SingleMetricDateMatrixFormContainer = ({
  gadget,
  onChange,
}: {
  gadget?: VisualisationDefinitions.SingleMetricDateMatrix;
  onChange?: React.Dispatch<
    React.SetStateAction<VisualisationDefinition | undefined>
  >;
}) => {
  const { currentFiscalYear } = useContext(FiscalPeriodsContext);

  const [id] = useState(gadget ? gadget.id : aguid());
  const [name, setName] = useState<string>(gadget ? gadget.name : '');
  const [description, setDescription] = useState<string>(
    gadget ? gadget.description : '',
  );
  const [metricId, setMetricId] = useState<string | undefined>(
    gadget ? gadget.metricId : undefined,
  );
  const [metricDisplayName, setMetricDisplayName] = useState<string>(
    gadget && gadget.metricDisplayName ? gadget.metricDisplayName : '',
  );
  const [interval, setInterval] = useState<FleetOps.Interval | FiscalInterval>(
    gadget ? gadget.interval : 'day',
  );
  const [clientSortBy, setClientSortBy] = useState<
    VisualisationDefinitions.ClientSortBy | undefined
  >(gadget ? gadget.clientSortBy : 'improvement (asc)');
  const [serverSortBy, setServerSortBy] =
    useState<VisualisationDefinitions.ServerSortBy>(
      gadget && gadget.serverSortBy && !gadget.serverSortBy.includes('change')
        ? gadget.serverSortBy
        : 'metric (asc)',
    );
  const [limit, setLimit] = useState<string>(
    gadget && gadget.limit ? gadget.limit.toString() : '20',
  );
  const [groupByField, setGroupByField] = useState<string | undefined>(
    gadget ? gadget.groupByField : undefined,
  );
  const [comparisonCellFormat, setComparisonCellFormat] =
    useState<MatrixCellType>(gadget ? gadget.comparisonCellFormat : 'delta');
  const [isSizedToFitOverridden, setIsSizedToFitOverridden] = useState<
    boolean | undefined
  >(gadget ? gadget.isSizedToFitOverridden : false);
  const isPreviewReady = groupByField !== undefined && metricId !== undefined;
  const isValid = isPreviewReady && name !== '';
  const [isLoading] = useState<boolean>(false);

  const onNameChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const onMetricDisplayNameChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setMetricDisplayName(event.target.value);
  };

  const onDescriptionChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value);
  };

  const onLimitChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setLimit(event.target.value);
  };

  const onIsSizedToFitChange = (newValue: boolean) => {
    setIsSizedToFitOverridden(newValue);
  };

  useEffect(() => {
    const newDraftGadget = (() => {
      if (groupByField === undefined || metricId === undefined || name === '') {
        return undefined;
      } else {
        return {
          id,
          name,
          description,
          type: 'SingleMetricDateMatrix' as VisualisationDefinitions.GadgetType,
          metricId,
          metricDisplayName,
          interval,
          groupByField,
          limit: Number.parseInt(limit),
          serverSortBy,
          clientSortBy,
          comparisonCellFormat,
          isSizedToFitOverridden,
        };
      }
    })();

    if (onChange) {
      onChange(newDraftGadget);
    }
  }, [
    clientSortBy,
    comparisonCellFormat,
    description,
    groupByField,
    id,
    interval,
    isSizedToFitOverridden,
    limit,
    metricDisplayName,
    metricId,
    name,
    onChange,
    serverSortBy,
  ]);

  return (
    <SingleMetricDateMatrixForm
      name={name}
      onNameChanged={onNameChanged}
      description={description}
      onDescriptionChanged={onDescriptionChanged}
      metricId={metricId}
      setMetricId={setMetricId}
      interval={interval}
      setInterval={setInterval}
      clientSortBy={clientSortBy}
      setClientSortBy={setClientSortBy}
      serverSortBy={serverSortBy}
      setServerSortBy={setServerSortBy}
      groupBy={groupByField}
      setGroupBy={setGroupByField}
      limit={limit}
      onLimitChanged={onLimitChanged}
      comparisonCellFormat={comparisonCellFormat}
      setComparisonCellFormat={setComparisonCellFormat}
      isValid={isValid}
      isEditing={!!gadget}
      isLoading={isLoading}
      metricDisplayName={metricDisplayName}
      onMetricDisplayNameChanged={onMetricDisplayNameChanged}
      setMetricDisplayName={setMetricDisplayName}
      isSizedToFitOverridden={isSizedToFitOverridden}
      onIsSizedToFitChange={onIsSizedToFitChange}
      isFiscalDisabled={!currentFiscalYear}
    />
  );
};

export default SingleMetricDateMatrixFormContainer;
